<template>
  <!--
    Génération des certificats.

    Un composant unique sert les deux cas d'usage :
      • toute une classe  (liste d'élèves)
      • un seul élève     (fiche élève)

    Chaque feuille est produite en résolvant les {{variables}} avec les
    DONNÉES RÉELLES de l'élève (registration_number, first_name, last_name,
    gender, dob, pob) et de sa classe (grade_code, speciality_code).
  -->
  <!--
    Téléporté en enfant direct de <body> : à l'impression, tout le reste de
    l'application peut alors être retiré du flux avec un simple
    `body > *:not(.cert-gen-overlay)`, sans dépendre de l'endroit d'où le
    modal a été ouvert (liste de classe ou fiche élève).
  -->
  <teleport to="body">
  <div v-if="open" class="cert-gen-overlay" @click.self="close">
    <div class="cert-gen-modal">
      <!-- ============================ En-tête ============================ -->
      <div class="gen-head cert-hide-print">
        <div>
          <h6 class="mb-1">{{ $t("certificate_gen.title") }}</h6>
          <p class="mb-0 text-xs" style="opacity: 0.6">
            {{ $t("certificate_gen.count", { count: sheets.length }) }}
            <span v-if="contextLabel"> — {{ contextLabel }}</span>
          </p>
        </div>
        <button type="button" class="icon-close" @click="close">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>

      <!-- ===================== Avertissement données ===================== -->
      <div class="gen-warning cert-hide-print" v-if="incomplete.length">
        <i class="bi bi-exclamation-triangle-fill me-2"></i>
        <div>
          <p class="mb-1 text-sm font-weight-bold">{{ $t("certificate_gen.missing_title") }}</p>
          <p class="mb-1 text-xs" style="opacity: 0.75">
            {{ $t("certificate_gen.missing_text") }}
          </p>
          <p class="mb-0 text-xs">
            <span class="missing-chip" v-for="item in incomplete.slice(0, 6)" :key="item.name">
              {{ item.name }} — {{ item.fields }}
            </span>
            <span v-if="incomplete.length > 6" class="text-xs" style="opacity: 0.6">
              +{{ incomplete.length - 6 }}
            </span>
          </p>
        </div>
      </div>

      <!-- ========================= Aperçu / rendu ========================= -->
      <div class="gen-body">
        <!-- Collecte des élèves en cours (un appel API par classe) -->
        <div class="gen-loading cert-hide-print" v-if="loading">
          <div class="spinner"></div>
          <p class="mb-0 text-sm">{{ $t("certificate_gen.loading") }}</p>
        </div>

        <div class="cert-print-area cert-print-batch" v-show="!loading">
          <div class="gen-page" v-for="sheet in sheets" :key="sheet.id">
            <certificate-sheet :data="sheet.certificate" />
          </div>
        </div>
        <p v-if="!sheets.length && !loading" class="text-center py-6" style="opacity: 0.6">
          {{ $t("certificate_gen.empty") }}
        </p>
      </div>

      <!-- ============================ Actions ============================ -->
      <div class="gen-actions cert-hide-print">
        <button type="button" class="btn-ghost" @click="close">
          {{ $t("button.cancel") }}
        </button>
        <button type="button" class="btn-primary-soft" :disabled="!sheets.length || loading" @click="print">
          <i class="bi bi-printer me-1"></i>{{ $t("certificate_gen.print") }}
        </button>
      </div>
    </div>
  </div>
  </teleport>
</template>

<script>
import CertificateSheet from "@/views/Classroom/CertificateSheet.vue";
import { classroomSetup } from "@/utils/classroomSetup";
import { resolveCertificate } from "@/utils/certificateModel";
import { loadSchoolProfile, schoolProfile } from "@/utils/schoolProfile";

export default {
  name: "certificate-generator",
  components: { CertificateSheet },
  props: {
    open: { type: Boolean, default: false },
    /** Élèves tels que renvoyés par l'API (aucune transformation attendue). */
    students: { type: Array, default: () => [] },
    /** Classe telle que renvoyée par l'API : { grade_code, speciality_code }. */
    classroom: { type: Object, default: () => ({}) },
    /**
     * Génération multi-classes : [{ classroom, students }].
     * Chaque élève est alors résolu avec SA propre classe — indispensable pour
     * que {{classe}} soit juste quand on imprime tout l'établissement d'un coup.
     * Prioritaire sur `students` / `classroom` lorsqu'il est renseigné.
     */
    groups: { type: Array, default: () => [] },
    /** Affiché pendant la collecte des élèves (plusieurs appels API). */
    loading: { type: Boolean, default: false },
  },
  emits: ["close"],
  data() {
    return { school: schoolProfile };
  },
  computed: {
    contextLabel() {
      if (this.groups && this.groups.length) {
        return this.$t("certificate_gen.classes_count", { count: this.groups.length });
      }
      const grade = this.classroom && this.classroom.grade_code;
      return grade ? String(grade) : "";
    },
    /**
     * Aplatit les entrées en paires (élève, sa classe), quel que soit le mode
     * d'appel : une seule classe ou plusieurs.
     */
    entries() {
      if (this.groups && this.groups.length) {
        return this.groups.flatMap((group) =>
          (group.students || [])
            .filter(Boolean)
            .map((student) => ({ student, classroom: group.classroom || {} }))
        );
      }
      return (this.students || [])
        .filter(Boolean)
        .map((student) => ({ student, classroom: this.classroom }));
    },
    /** Une feuille résolue par élève. */
    sheets() {
      const certificate = classroomSetup.certificate;
      return this.entries.map(({ student, classroom }, index) => {
        const { certificate: resolved, missing } = resolveCertificate({
          certificate,
          student,
          classroom,
          school: this.school,
          locale: this.$i18n.locale,
        });
        return {
          id: `${student.registration_number || "student"}-${index}`,
          name: [student.first_name, student.last_name].filter(Boolean).join(" "),
          certificate: resolved,
          missing,
        };
      });
    },
    /** Élèves dont au moins une donnée réelle manque : signalé avant impression. */
    incomplete() {
      return this.sheets
        .filter((sheet) => sheet.missing.length)
        .map((sheet) => ({
          name: sheet.name || sheet.id,
          fields: sheet.missing
            .map((key) => this.$t(`certificate_gen.field_${key}`))
            .join(", "),
        }));
    },
  },
  watch: {
    open: {
      immediate: true,
      handler(value) {
        if (value) {
          loadSchoolProfile();
        }
        // On empêche le défilement de l'arrière-plan pendant l'aperçu.
        document.body.style.overflow = value ? "hidden" : "";
        if (value) this.bindPrintEvents();
        else this.unbindPrintEvents();
      },
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
    this.unbindPrintEvents();
  },
  methods: {
    close() {
      this.$emit("close");
    },
    /**
     * Pose le marqueur qui active les règles @media print de ce composant.
     *
     * Déclenché par `beforeprint`, et non depuis le bouton : l'impression
     * peut aussi être lancée au clavier (Ctrl+P) ou par le menu du
     * navigateur. En passant par l'événement, tous les chemins sont couverts.
     */
    markPrinting() {
      document.body.classList.add("cert-printing-batch");
    },
    unmarkPrinting() {
      document.body.classList.remove("cert-printing-batch");
    },
    /**
     * Safari n'émet pas `beforeprint` / `afterprint` : il signale l'entrée et
     * la sortie du mode impression via la media query.
     */
    onPrintMediaChange(event) {
      if (event.matches) this.markPrinting();
      else this.unmarkPrinting();
    },
    bindPrintEvents() {
      window.addEventListener("beforeprint", this.markPrinting);
      window.addEventListener("afterprint", this.unmarkPrinting);
      if (window.matchMedia) {
        this.printMedia = window.matchMedia("print");
        if (this.printMedia.addEventListener) {
          this.printMedia.addEventListener("change", this.onPrintMediaChange);
        }
      }
    },
    unbindPrintEvents() {
      window.removeEventListener("beforeprint", this.markPrinting);
      window.removeEventListener("afterprint", this.unmarkPrinting);
      if (this.printMedia && this.printMedia.removeEventListener) {
        this.printMedia.removeEventListener("change", this.onPrintMediaChange);
      }
      this.unmarkPrinting();
    },
    print() {
      // Le marqueur est posé par `beforeprint`. Aucun retrait programmé ici :
      // un `setTimeout` retirait la classe pendant que l'aperçu d'impression
      // était encore ouvert, et l'aperçu, qui se met à jour en direct,
      // réaffichait alors toute la page derrière le certificat.
      window.print();
    },
  },
};
</script>

<style scoped>
.cert-gen-overlay {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(8, 12, 24, 0.72);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.cert-gen-modal {
  width: min(100%, 980px);
  max-height: min(88vh, 900px);
  height: auto;
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  background: #1b2138;
  border: 1px solid rgba(255, 255, 255, 0.12);
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.25);
}

.gen-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.09);
  flex-shrink: 0;
}

.icon-close {
  background: transparent;
  border: none;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  font-size: 15px;
}

.icon-close:hover {
  opacity: 1;
}

.gen-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 12px 20px 0;
  padding: 12px 14px;
  border-radius: 10px;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fbbf24;
  flex-shrink: 0;
}

.missing-chip {
  display: inline-block;
  background: rgba(251, 191, 36, 0.16);
  border-radius: 5px;
  padding: 2px 7px;
  margin: 2px 4px 2px 0;
  font-size: 10.5px;
}

.gen-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
  scrollbar-gutter: stable;
  overscroll-behavior: contain;
}

.gen-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 60px 0;
  opacity: 0.75;
}

.spinner {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: #2ecebf;
  animation: cert-spin 0.8s linear infinite;
}

@keyframes cert-spin {
  to {
    transform: rotate(360deg);
  }
}

.cert-print-area {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gen-page {
  width: 100%;
}

.gen-page + .gen-page {
  margin-top: 0;
}

.gen-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.09);
  flex-shrink: 0;
}

.btn-ghost {
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: inherit;
  border-radius: 9px;
  padding: 9px 20px;
  font-size: 13px;
  cursor: pointer;
}

.btn-primary-soft {
  border: none;
  background: #2ecebf;
  color: #06231f;
  border-radius: 9px;
  padding: 9px 22px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.btn-primary-soft:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>

<style>
/* Non scopé : à l'impression, chaque certificat occupe exactement une page A4. */
/*
 * IMPRESSION — trois points sensibles, chacun à l'origine d'un bug constaté.
 *
 * 1. `!important` est indispensable sur .cert-gen-overlay et .cert-gen-modal.
 *    Le <style scoped> plus haut compile en `.cert-gen-overlay[data-v-xxx]`
 *    (spécificité 0,2,0) et l'emportait sur ce bloc non scopé (0,1,0) :
 *    l'overlay restait `position: fixed`, or un élément fixed s'imprime tel
 *    qu'affiché à l'écran, sur une seule page. C'est ce qui sortait le modal
 *    à la place des certificats.
 *
 * 2. Le reste de la page est retiré avec `display: none`, pas
 *    `visibility: hidden` : un élément seulement invisible occupe toujours sa
 *    place et générait des pages blanches avant les certificats.
 *
 * 3. Tout est conditionné à `body.cert-printing-batch`, posé par print().
 *    Ce bloc et celui de CertificateDesigner.vue sont tous deux globaux : dès
 *    que les deux écrans ont été visités dans la même session, leurs règles
 *    coexistent. Sans ce marqueur, chacune cassait l'impression de l'autre.
 */
@media print {
  @page {
    size: A4 portrait;
    margin: 0;
  }

  body.cert-printing-batch {
    height: auto !important;
    overflow: visible !important;
    background: #ffffff !important;
  }

  body.cert-printing-batch > *:not(.cert-gen-overlay) {
    display: none !important;
  }

  body.cert-printing-batch .cert-gen-overlay {
    position: static !important;
    inset: auto !important;
    display: block !important;
    background: transparent !important;
    padding: 0 !important;
    overflow: visible !important;
    z-index: auto !important;
  }

  body.cert-printing-batch .cert-gen-modal {
    width: 100% !important;
    max-width: none !important;
    max-height: none !important;
    height: auto !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    background: transparent !important;
    overflow: visible !important;
    display: block !important;
  }

  body.cert-printing-batch .gen-head,
  body.cert-printing-batch .gen-warning,
  body.cert-printing-batch .gen-actions,
  body.cert-printing-batch .gen-loading,
  body.cert-printing-batch .cert-hide-print {
    display: none !important;
  }

  body.cert-printing-batch .gen-body {
    padding: 0 !important;
    overflow: visible !important;
    min-height: 0 !important;
    flex: none !important;
  }

  body.cert-printing-batch .cert-print-batch {
    display: block !important;
    width: 100% !important;
    gap: 0 !important;
  }

  /* Une feuille = une page A4, jamais coupée en deux. */
  body.cert-printing-batch .cert-print-batch .gen-page {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    page-break-after: always;
    break-after: page;
    page-break-inside: avoid;
    break-inside: avoid;
  }

  body.cert-printing-batch .cert-print-batch .gen-page:last-child {
    page-break-after: auto;
    break-after: auto;
  }

  /* .cert-shell / .cert-sheet viennent du <style scoped> de
     CertificateSheet.vue : `!important` est nécessaire pour passer devant. */
  body.cert-printing-batch .cert-print-batch .cert-shell {
    max-width: none !important;
    margin: 0 !important;
  }

  body.cert-printing-batch .cert-print-batch .cert-sheet {
    width: 100% !important;
    box-shadow: none !important;
    border-radius: 0 !important;
  }
}
</style>
